import type { ProductCategoryDto } from '~/utils/appointment'
import type { AsyncRpcOptions } from '~/utils/types'

export function useAppointments(
  options: AsyncRpcOptions<'getAppointments'> = {},
  key?: string,
) {
  const { $currentShop, $fimLocalePath } = useNuxtApp()
  const { user } = useUser()
  const promise = useAsyncRpc(
    'getAppointments',
    undefined,
    { dedupe: 'defer', server: false, immediate: false, ...options },
    key ?? `useAppointments-${$currentShop.id}`,
  )

  const { data, refresh: fetch, status } = promise
  const fetching = computed(() => status.value === 'pending')

  const getAppointmentUrl = (service: string = ServiceCategoryDto.GL_CA) => {
    const domain = $currentShop.appointmentDomain
    const baseFolder = $currentShop.appointmentDomainLocaleFolder ?? ''

    if (domain) {
      const url = normalizeUrl([baseFolder, '/find-branch'], domain)

      url.searchParams.set('service', service)
      return url.toString()
    }

    console.error(
      `Error: missing appointment domain for locale "${$currentShop.locale}"`,
    )

    return $fimLocalePath(routeList.stores)
  }

  const getAppointmentInBranchUrl = (
    branchId: string,
    service?: ProductCategoryDto,
  ) => {
    const domain = $currentShop.appointmentDomain
    const baseFolder = $currentShop.appointmentDomainLocaleFolder ?? ''

    if (domain) {
      const url = normalizeUrl([baseFolder, '/service'], domain)

      url.searchParams.set('branch-id', branchId)
      if (service) {
        url.searchParams.set('product-category', service)
      }
      return url.toString()
    }

    console.error(
      `Error: missing appointment domain for locale "${$currentShop.locale}"`,
    )

    return $fimLocalePath(routeList.stores)
  }

  const getAppointmentServiceUrl = (service: ProductCategoryDto) => {
    const domain = $currentShop.appointmentDomain
    const baseFolder = $currentShop.appointmentDomainLocaleFolder ?? ''

    if (domain) {
      const url = normalizeUrl([baseFolder, '/service'], domain)
      url.searchParams.set('product-category', service)
      return url.toString()
    }

    console.error(
      `Error: missing appointment domain for locale "${$currentShop.locale}"`,
    )

    return $fimLocalePath(routeList.stores)
  }
  const getDeleteAppointmentUrl = (appointment: Appointment) => {
    if (appointment.inPast || !appointment.cancelable) {
      return ''
    }
    const appointmentUrl = normalizeUrl(
      [$currentShop.appointmentDomainLocaleFolder, 'cancellation'],
      $currentShop.appointmentDomain,
    )
    appointmentUrl.searchParams.set('appointmentId', appointment.id)
    appointmentUrl.searchParams.set(
      'cancellationToken',
      appointment.cancellationToken,
    )
    appointmentUrl.searchParams.set('branchId', appointment.branch.id)
    appointmentUrl.searchParams.set(
      'productCategory',
      appointment.service.productCategory,
    )
    return appointmentUrl.toString()
  }

  const showAppointments = computed(
    () =>
      Boolean($currentShop.appointmentDomain) && isIdpUser(user, $currentShop),
  )

  const getAppointmentACUrl = () => {
    const domain = $currentShop.appointmentDomain
    const path = $currentShop.appointmentPathAc

    if (domain && path) {
      const url = normalizeUrl([path], domain)

      return url.toString()
    }

    console.error(
      `Error: missing appointment domain or path for locale "${$currentShop.locale}"`,
    )

    return undefined
  }

  return extendPromise(
    promise.then(() => ({})),
    {
      data,
      fetch,
      fetching,
      getAppointmentUrl,
      getDeleteAppointmentUrl,
      getAppointmentInBranchUrl,
      getAppointmentServiceUrl,
      showAppointments,
      getAppointmentACUrl,
    },
  )
}
